import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { Invoice } from '../types';
import { invoiceAPI } from '../services/api';
import InvoicePDF from './InvoicePDF';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [exportDesktopLoading, setExportDesktopLoading] = useState(false);
  const [exportOnlineLoading, setExportOnlineLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [clearingQboLink, setClearingQboLink] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchInvoice(parseInt(id));
    }
  }, [id]);

  const fetchInvoice = async (invoiceId: number) => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getById(invoiceId);
      setInvoice(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch invoice');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportQuickbooksDesktop = async () => {
    const invoiceId = invoice?.id;
    if (invoiceId == null) return;
    setExportMessage(null);
    setExportDesktopLoading(true);
    try {
      const response = await invoiceAPI.exportQuickbooksDesktop(invoiceId);
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice?.invoice_number ?? invoiceId}.iif`;
      a.click();
      window.URL.revokeObjectURL(url);
      setExportMessage('IIF file downloaded. Import in QuickBooks Desktop via File → Utilities → Import → IIF.');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: unknown; status?: number } };
      const data = ax?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text) as { error?: string };
          setExportMessage(j?.error || 'Export failed.');
        } catch {
          setExportMessage('Export failed.');
        }
      } else {
        const msg = typeof data === 'object' && data !== null && 'error' in data
          ? String((data as { error: string }).error)
          : 'Export failed.';
        setExportMessage(msg);
      }
    } finally {
      setExportDesktopLoading(false);
    }
  };

  const handleExportQuickbooksOnline = async () => {
    const invoiceId = invoice?.id;
    if (invoiceId == null) return;
    setExportMessage(null);
    setExportOnlineLoading(true);
    try {
      const response = await invoiceAPI.exportQuickbooksOnline(invoiceId);
      const respData = response.data as { message?: string; invoice_number?: string; quickbooks_doc_number?: string };
      let msg = respData?.message || 'Invoice exported to QuickBooks Online.';
      if (respData?.quickbooks_doc_number) {
        msg += ` Invoice #${respData.quickbooks_doc_number}`;
      }
      setExportMessage(msg);
      fetchInvoice(invoiceId);
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          data?: unknown;
          status?: number;
          statusText?: string;
        };
      };
      const data = ax?.response?.data;
      let msg = 'Export failed.';
      if (data != null && typeof data === 'object' && 'error' in data) {
        msg = String((data as { error?: string }).error ?? msg);
      } else if (data != null && typeof data === 'object' && 'detail' in data) {
        msg = String((data as { detail?: string }).detail ?? msg);
      } else if (typeof data === 'string' && data) {
        msg = data;
      } else if (ax?.response?.statusText) {
        msg = `${msg} (${ax.response.statusText})`;
      }
      setExportMessage(msg);
      if (process.env.NODE_ENV !== 'production') {
        console.error('QuickBooks export error:', ax?.response?.status, data, err);
      }
    } finally {
      setExportOnlineLoading(false);
    }
  };

  const handleClearQboLink = async () => {
    const invoiceId = invoice?.id;
    if (invoiceId == null) return;
    setClearingQboLink(true);
    setExportMessage(null);
    try {
      const response = await invoiceAPI.clearQboLink(invoiceId);
      setExportMessage(response.data.message);
      fetchInvoice(invoiceId);
    } catch {
      setExportMessage('Failed to clear QBO link.');
    } finally {
      setClearingQboLink(false);
    }
  };

  const handleDeleteInvoice = async () => {
    const invoiceId = invoice?.id;
    if (invoiceId == null) return;
    setDeleting(true);
    try {
      await invoiceAPI.delete(invoiceId);
      window.location.href = '/invoices';
    } catch {
      setExportMessage('Failed to delete invoice.');
      setShowDeleteWarning(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <p>Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="container">
        <div className="card">
          <div className="error">{error || 'Invoice not found'}</div>
          <Link to="/invoices" className="btn btn-secondary">
            Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Action Buttons */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Link to="/invoices" className="btn btn-secondary">
                ← Back to Invoices
              </Link>
              <Link to={`/invoices/${invoice.id}/edit`} className="btn btn-primary">
                Edit Invoice
              </Link>
              <button
                type="button"
                onClick={() => {
                  if (invoice.qbo_invoice_id) {
                    setShowDeleteWarning(true);
                  } else if (window.confirm('Are you sure you want to delete this invoice?')) {
                    handleDeleteInvoice();
                  }
                }}
                disabled={deleting}
                className="btn"
                style={{ backgroundColor: '#e74c3c', color: '#fff' }}
              >
                {deleting ? 'Deleting...' : 'Delete Invoice'}
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                onClick={() => setShowPDFPreview(!showPDFPreview)}
                className="btn btn-secondary"
              >
                {showPDFPreview ? 'Hide Preview' : 'Preview PDF'}
              </button>
              <PDFDownloadLink
                document={<InvoicePDF invoice={invoice} />}
                fileName={`invoice-${invoice.invoice_number}.pdf`}
                className="btn btn-success"
              >
                {({ loading }) => <>{loading ? 'Preparing PDF...' : 'Download PDF'}</>}
              </PDFDownloadLink>
            </div>
          </div>

          {/* QBO Export Status */}
          {invoice.qbo_invoice_id && (
            <div
              style={{
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                backgroundColor: '#e8f4fd',
                border: '1px solid #b3d9f2',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <span>
                <strong>QBO Exported</strong>
                {' — '}QBO Invoice ID: {invoice.qbo_invoice_id}
                {invoice.qbo_exported_at && (
                  <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                    (exported {new Date(invoice.qbo_exported_at).toLocaleString()})
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={handleClearQboLink}
                disabled={clearingQboLink}
                className="btn btn-secondary"
                style={{ fontSize: '0.85rem', padding: '0.25rem 0.75rem' }}
                title="Clear the QBO link so this invoice can be re-exported. Does NOT delete the invoice from QBO."
              >
                {clearingQboLink ? 'Clearing...' : 'Clear QBO Link'}
              </button>
            </div>
          )}

          <div style={{ borderTop: '1px solid #eee', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Export to QuickBooks</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={handleExportQuickbooksDesktop}
                disabled={exportDesktopLoading || !invoice.line_items?.length}
                className="btn btn-secondary"
                title={!invoice.line_items?.length ? 'Add at least one line item to export' : 'Download IIF for QuickBooks Desktop'}
              >
                {exportDesktopLoading ? 'Preparing...' : 'Download for QuickBooks Desktop'}
              </button>
              <button
                type="button"
                onClick={handleExportQuickbooksOnline}
                disabled={exportOnlineLoading || !invoice.line_items?.length || !!invoice.qbo_invoice_id}
                className="btn btn-secondary"
                title={
                  invoice.qbo_invoice_id
                    ? 'Already exported to QBO. Clear the QBO link to re-export.'
                    : !invoice.line_items?.length
                      ? 'Add at least one line item to export'
                      : 'Export to QuickBooks Online'
                }
              >
                {exportOnlineLoading ? 'Exporting...' : invoice.qbo_invoice_id ? 'Already Exported to QBO' : 'Export to QuickBooks Online'}
              </button>
            </div>
          </div>
        </div>

        {/* Delete Warning Modal */}
        {showDeleteWarning && (
          <div
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                padding: '2rem',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              }}
            >
              <h3 style={{ marginTop: 0, color: '#e74c3c' }}>Warning: Invoice Exported to QBO</h3>
              <p>
                This invoice was exported to QuickBooks Online
                (QBO Invoice ID: {invoice.qbo_invoice_id}).
              </p>
              <p>
                Deleting it here will <strong>NOT</strong> delete it from QBO.
                You will need to manually delete or void it in QuickBooks Online as well.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowDeleteWarning(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDeleteWarning(false); handleDeleteInvoice(); }}
                  disabled={deleting}
                  className="btn"
                  style={{ backgroundColor: '#e74c3c', color: '#fff' }}
                >
                  {deleting ? 'Deleting...' : 'Delete Anyway'}
                </button>
              </div>
            </div>
          </div>
        )}

        {exportMessage && (
          <div
            className="card"
            style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: exportMessage.includes('failed') || exportMessage.includes('not connected')
                ? '#fff3cd'
                : '#d4edda',
              border: `1px solid ${exportMessage.includes('failed') || exportMessage.includes('not connected') ? '#ffc107' : '#28a745'}`,
              borderRadius: '4px',
            }}
          >
            {exportMessage}
            <button
              type="button"
              onClick={() => setExportMessage(null)}
              style={{ marginLeft: '1rem', padding: '0.25rem 0.5rem' }}
              aria-label="Dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* PDF Preview */}
        {showPDFPreview && (
          <div style={{ marginBottom: '2rem', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
            <PDFViewer width="100%" height="600px">
              <InvoicePDF invoice={invoice} />
            </PDFViewer>
          </div>
        )}

        {/* Invoice Header */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Invoice {invoice.invoice_number || 'Draft'}</h2>
            <span className={`status-badge status-${invoice.status}`}>{invoice.status}</span>
          </div>
        </div>

        {/* Invoice Details Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          {/* Bill To Section */}
          <div>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Bill To</h3>
            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <p style={{ margin: '0.25rem 0', fontWeight: 'bold' }}>{invoice.customer_details?.name}</p>
              {invoice.customer_details?.address_line1 && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.address_line1}</p>
              )}
              {invoice.customer_details?.address_line2 && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.address_line2}</p>
              )}
              {invoice.customer_details?.address_line3 && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.address_line3}</p>
              )}
              <p style={{ margin: '0.25rem 0' }}>
                {invoice.customer_details?.city}, {invoice.customer_details?.state}{' '}
                {invoice.customer_details?.zip_code}
              </p>
              {invoice.customer_details?.email && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.email}</p>
              )}
              {invoice.customer_details?.phone && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.phone}</p>
              )}
            </div>
          </div>

          {/* Invoice Details Section */}
          <div>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Invoice Details</h3>
            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Invoice Number:</strong>
                <span>{invoice.invoice_number || 'Draft'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>PO Number:</strong>
                <span>
                  {invoice.po_details?.po_number}
                  {invoice.exclude_po_from_export && (
                    <span style={{ color: '#999', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                      (not sent to QBO)
                    </span>
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Issue Date:</strong>
                <span>{formatDate(invoice.issue_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Due Date:</strong>
                <span>{formatDate(invoice.due_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Payment Terms:</strong>
                <span>{invoice.po_details?.payment_terms_display}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Line Items</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Unit Price</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.description}</td>
                    <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(Number(item.unit_price))}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.total || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals Section */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2rem' }}>
          <div style={{ width: '300px', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
              <span>Subtotal:</span>
              <strong>{formatCurrency(invoice.subtotal || 0)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
              <span>Tax ({invoice.tax_rate}%):</span>
              <strong>{formatCurrency(invoice.tax_amount || 0)}</strong>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                margin: '0.5rem 0',
                paddingTop: '0.5rem',
                borderTop: '2px solid #2c3e50',
                fontSize: '1.2rem',
              }}
            >
              <strong>Total Due:</strong>
              <strong style={{ color: '#27ae60' }}>{formatCurrency(invoice.total_amount || 0)}</strong>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        {invoice.notes && (
          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h3 style={{ marginBottom: '0.5rem', color: '#2c3e50' }}>Notes</h3>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail;
